/**
 * アップロード待ちの録画（Issue #736）: 止まって試合に付いているのに、まだ本番へ上がっていない録画の一覧（`GetRecordingUploads`）。
 * 録画は機器（TetherCam / OBS のスクリプト）が補正サーバー経由で本番の署名付き URL へ上げ、上がったら本番が config を作って
 * field_gen を積む。ジョブが failed で残る代わりに、上がらなかった録画がここに残る（失敗の見え方）。30 秒ごとに見直す。
 * 上げない録画は「消す」（`DiscardRecordingUpload`。Issue #739）。本番で上げないことにするだけで、機器の手元のファイルは消えない。
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/api';

export const STATUS_LABELS = { pending: 'アップロード待ち', uploading: 'アップロード中' };

export function uploadLabel(u) {
  const name = [u.duel_name, u.game_name].filter(Boolean).join(' ') || u.game_uuid;
  const src = u.source === 'obs' ? 'OBS' : u.source === 'seat1' ? 'P1' : u.source === 'seat2' ? 'P2' : '';
  return `${name}${src ? `（${src}）` : ''} ${u.recording_key}`;
}

export function stoppedText(u) {
  const ms = u.stopped_unix_ms ?? u.started_unix_ms;
  if (!ms) return '';
  const d = new Date(Number(ms));
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function RecordingUploadsPanel() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.project('GetRecordingUploads', {});
      setItems(Array.isArray(r) ? r : (r?.data ?? []));
      setError(null);
    } catch (e) {
      setError(String(e?.message ?? e));
    }
  }, []);
  const discard = useCallback(async (u) => {
    if (!window.confirm(`${uploadLabel(u)} を上げずに一覧から消します（機器の手元のファイルは消えません）。よろしいですか？`)) return;
    try {
      await api.project('DiscardRecordingUpload', { recording_key: u.recording_key });
      setItems((list) => (list ?? []).filter((x) => x.recording_key !== u.recording_key));
      setError(null);
    } catch (e) {
      setError(String(e?.message ?? e));
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (items === null) return null;
  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="recording-uploads">
      <div className="card-title" style={{ marginBottom: 4 }}>アップロード待ちの録画</div>
      <div className="card-meta" style={{ marginBottom: 6 }}>
        止めた録画は機器が本番へ上げ、上がるとフィールド生成を積む。ここに残っているものはまだ上がっていない（TetherCam / OBS の配信をオンにして待つ）
      </div>
      {items.length === 0 ? <div className="card-meta">ありません</div> : (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {items.map((u) => (
            <li key={u.id} data-testid="recording-upload" data-status={u.upload_status}>
              {uploadLabel(u)}
              <span className="card-meta" style={{ marginLeft: 8 }}>{stoppedText(u)} {STATUS_LABELS[u.upload_status] ?? u.upload_status}</span>
              <button type="button" className="btn btn-sm btn-secondary" style={{ marginLeft: 8 }} data-testid="recording-upload-discard"
                onClick={() => discard(u)}>消す</button>
            </li>
          ))}
        </ul>
      )}
      {error ? <div role="status" style={{ color: '#f99', marginTop: 8 }}>{error}</div> : null}
    </div>
  );
}
